import { useEffect, useState } from "react";
import toast from "react-hot-toast";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { queryClient } from "@/App";
import { Modal, ModalContent } from "@/atoms-react/modal/index.react";
import Stepper from "@/atoms-react/Stepper";
import { SectionTitle } from "@/atoms-react/text/Text.react";
import { useAdmin } from "@/features/admin/hooks/use-admin";
import { LegalEntityAdminCreateSchema } from "@/features/admin/types";
import { useRefreshUser } from "@/features/auth/hooks/use-refresh-user";
import { role } from "@/features/auth/types";
import { useCompany } from "@/features/company/hooks/use-company";
import {
  AgentNatureMandate,
  CheckJoinLegalEntityResponse,
  CompanyResponse,
  LegalEntityCreateSchema,
  RCSRepresentative,
  rcsResponseType,
  OlkyPassProLinkType,
  LegalEntityLinkDto
} from "@/features/company/types";
import { useOrigin } from "@/features/flow/hooks/use-origin";
import { DeclarativeNifCompany } from "@/features/flow/types";
import { Routes } from "@/features/router";
import { useUser } from "@/features/user/hooks/use-user";
import { useNifValidation } from "@/features/user/nif/hooks/use-nif-validation";
import { FullCountry } from "@/features/user/types";
import { useLocale } from "@/features/utils/hooks/use-locale";
import { useAppSelector } from "@/redux/hooks";
import { getUserRoles } from "@/redux/slices/userSlice";
import { extractErrorDescription } from "@/utils/internal-code-handler";
import { AddCompanyDetected } from "./AddCompanyDetected";
import { AddCompanyDetectRepresentative } from "./AddCompanyDetectRepresentative";
import { AddCompanyForm } from "./AddCompanyForm";
import { CompanyDetection } from "./CompanyDetection";
import { FinalStepCompanAdd } from "./FinalStepCompanyAdd";
import { RepresentativeForm } from "./RepresentativeForm";
import { QueryOptionsFactory } from "@/features/company/utils";

const countryNifChecked = [
  "AT",
  "BE",
  "BG",
  "CY",
  "CZ",
  "DE",
  "DK",
  "EE",
  "EL",
  "ES",
  "FI",
  "FR",
  "HR",
  "HU",
  "IE",
  "IT",
  "LT",
  "LU",
  "LV",
  "MT",
  "NL",
  "PL",
  "PT",
  "RO",
  "SE",
  "SI",
  "SK"
];
enum ModalAddCompanyModalEnum {
  STEP_1_START = "STEP_1_START",
  STEP_1_DETECTION = "STEP_1_DETECTION",
  STEP_1_NO_DETECTION = "STEP_1_NO_DETECTION",
  STEP_2_REPRESENTATIVE_LIST = "STEP_2_REPRESENTATIVE_LIST",
  STEP_2_REPRESENTATIVE_DETECTED = "STEP_2_REPRESENTATIVE_DETECTED",
  STEP_3_END = "STEP_3_END"
}
enum subStepes {
  SUBSTEP_1 = "SUBSTEP_1",
  SUBSTEP_2 = "SUBSTEP_2"
}
type addCompanyModalPropsType = {
  closeCompanyModal: () => void;
  open: boolean;
};
export const AddCompanyModal = ({ closeCompanyModal, open }: addCompanyModalPropsType) => {
  const translation = useTranslation(["pass", "error"]);
  const { t } = translation;
  const navigate = useNavigate();
  const [steps, setSteps] = useState<ModalAddCompanyModalEnum>();
  const [subSteps, setSubSteps] = useState<subStepes>();
  const userRoles = useAppSelector(getUserRoles);
  const isAdmin = userRoles.includes(role.ADMIN);

  const getSubStepStatus = (subSteps?: subStepes) => {
    if (!subSteps) return;
    switch (subSteps) {
      case subStepes.SUBSTEP_1:
        return [
          { id: "01", name: "Entreprise", status: "current" },
          { id: "02", name: "Relation du mandataire", status: "upcoming" }
        ];
      case subStepes.SUBSTEP_2:
        return [
          { id: "01", name: "Entreprise", status: "complete" },
          { id: "02", name: "Relation du mandataire", status: "current" }
        ];
    }
  };
  const { locale } = useLocale();
  const {
    addCompanyDataMutation,
    useJoinLegalEntity,
    useCheckJoinLegalEntity,
    fetchCompanyDetectedInfoRcs
  } = useCompany();
  const { addCompanyDataAdminMutation } = useAdmin();
  const { mutateAsync: createAdminCompany, isPending: isLoadingAdminCreation } =
    addCompanyDataAdminMutation();

  const { mutateAsync: createCompany, isPending: isLoadingCreation } = addCompanyDataMutation();

  const { mutateAsync: joinLegalEntityMutation, isPending: isLoadingJoinLegalEntity } =
    useJoinLegalEntity();
  const { mutateAsync: fetchCompanyRcs, isPending: isfetchCompanyRcs } =
    fetchCompanyDetectedInfoRcs();
  const { mutateAsync: checkJoinLegalEntityMutation, isPending: checkJoinLoadingLegalEntity } =
    useCheckJoinLegalEntity();
  const [companyData, setCompanyData] = useState<
    LegalEntityCreateSchema & { companyUuid?: string; link_type?: OlkyPassProLinkType }
  >();
  const { useActivitiesNace, useCountries, useNatureMandate, useUserProfile } = useUser();
  const { data: countries } = useCountries(locale.language);
  const { data: activitiesNace, isLoading: isLoadingNace } = useActivitiesNace(locale.language, {
    enabled: steps === ModalAddCompanyModalEnum.STEP_1_NO_DETECTION,
    queryKey: ["static-data", "activities", locale]
  });
  const [rcsCompany, setRcsCompany] = useState<rcsResponseType>();
  const [loadingFormCompany, setLoadingformCompany] = useState<boolean>(false);
  const [legalEntityCheckJoinResponse, setLegalEntityCheckJoinResponse] =
    useState<CheckJoinLegalEntityResponse>();
  const [companyResponse, setCompanyResponse] = useState<CompanyResponse>();
  const origin = useOrigin();
  const { refreshToken, isRefreshTokenLoading } = useRefreshUser();
  const [representative, setRepresentative] = useState<RCSRepresentative | null>(null);
  const { getNifValidation } = useNifValidation();
  const { mutateAsync: nififValidation } = getNifValidation();
  const { data: natureMandates, isPending: pendingNatureMandate } = useNatureMandate(
    locale.language,
    steps === ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED
  );
  useEffect(() => {
    if (!steps) setSteps(ModalAddCompanyModalEnum.STEP_1_START);
    if (!subSteps) setSubSteps(subStepes.SUBSTEP_1);
  }, []);
  const handleNifVerification = async (
    taxResidence: DeclarativeNifCompany
  ): Promise<DeclarativeNifCompany | null> => {
    const taxNifCountry = countries?.countries.find(
      (item: FullCountry) => item.id.toString() === taxResidence?.country_id
    );
    if (countryNifChecked.includes(taxNifCountry?.alpha2 ?? "")) {
      try {
        const response = await nififValidation({
          nif: taxResidence.tin ?? "",
          countryCode: taxNifCountry?.alpha2 ?? ""
        });
        if (response.validSyntax && response.validStructure) {
          taxResidence.tinCheck = true;
          taxResidence.apiCheck = true;
          taxResidence.tinExist = response.validSyntax && response.validStructure;
        } else {
          toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
          return null;
        }
      } catch (_) {
        toast.error(t("olkypass.mainPage.olkypass.modalOpenOlkyPay.steps.nif.error"));
        return null;
      }
    } else {
      taxResidence.tinCheck = true;
      taxResidence.apiCheck = true;
      taxResidence.tinExist = true;
    }
    return taxResidence;
  };

  const { data: user } = useUserProfile({
    enabled: !!rcsCompany && steps == ModalAddCompanyModalEnum.STEP_1_NO_DETECTION,
    queryKey: ["user", "profile"]
  });
  const findRCSRepresentative = async (rcsData: rcsResponseType) => {
    return rcsData?.representatives.find(
      (representative) =>
        (representative.last_name?.toLocaleLowerCase() ===
          user?.naturalPerson.LastName.toLocaleLowerCase() &&
          representative.first_name?.toLocaleLowerCase() ===
            user?.naturalPerson.FirstName.toLocaleLowerCase()) ||
        (representative.first_name?.toLocaleLowerCase() ===
          user?.naturalPerson.LastName.toLocaleLowerCase() &&
          representative.last_name?.toLocaleLowerCase() ===
            user?.naturalPerson.FirstName.toLocaleLowerCase())
    );
  };

  return (
    <Modal
      open={open}
      onClose={() => {
        closeCompanyModal();
        setRcsCompany(undefined);
        setRepresentative(null);
        setCompanyResponse(undefined);
        if (steps === ModalAddCompanyModalEnum.STEP_3_END) {
          queryClient.invalidateQueries({
            queryKey: QueryOptionsFactory.legalEntities().queryKey
          });
        }
        setSteps(ModalAddCompanyModalEnum.STEP_1_START);
      }}
      className="max-w-5xl"
    >
      <ModalContent>
        <div className="px-4">
          {steps === ModalAddCompanyModalEnum.STEP_3_END ? (
            <div className="flex w-full flex-row justify-center p-4">
              <SectionTitle>{t("business.title_business.company_added")}</SectionTitle>
            </div>
          ) : (
            <div className="flex w-full flex-row justify-center">
              <SectionTitle>{t("business.title_business.add_company")}</SectionTitle>
            </div>
          )}
          {steps === ModalAddCompanyModalEnum.STEP_1_START && (
            <CompanyDetection
              countries={countries?.countries as FullCountry[]}
              loading={checkJoinLoadingLegalEntity || isfetchCompanyRcs}
              onValidate={async (data: CompanyDetection | null) => {
                if (data === null) setSteps(ModalAddCompanyModalEnum.STEP_1_NO_DETECTION);
                data = { ...data, inscriptionNumber: data?.inscriptionNumber?.replace(/\s/g, "") };
                if (data.idCompany) {
                  try {
                    const response = await checkJoinLegalEntityMutation(data.idCompany as string);
                    setSteps(ModalAddCompanyModalEnum.STEP_1_DETECTION);
                    setLegalEntityCheckJoinResponse(response);
                  } catch (error: unknown) {
                    toast.error(extractErrorDescription(error, translation));
                  }
                } else if (data.inscriptionCountry && data.inscriptionNumber) {
                  fetchCompanyRcs({
                    country: data.inscriptionCountry.toLocaleLowerCase(),
                    rcs: data.inscriptionNumber
                  })
                    .then(async ({ data: rcsData }) => {
                      setRcsCompany(rcsData);
                    })
                    .finally(() => setSteps(ModalAddCompanyModalEnum.STEP_1_NO_DETECTION));
                } else {
                  setSteps(ModalAddCompanyModalEnum.STEP_1_NO_DETECTION);
                }
              }}
            />
          )}
          {steps === ModalAddCompanyModalEnum.STEP_1_DETECTION && (
            <>
              <div className="mt-2 mb-4 flex w-full flex-row justify-center">
                <SectionTitle>{t("business.title_business.company_add_section")}</SectionTitle>
              </div>
              <div className="px-6 py-3">
                <Stepper origin={origin} steps={getSubStepStatus(subSteps) ?? []} />
                <div className="px-2">
                  <AddCompanyDetected
                    onValidate={() => {
                      setSteps(ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED);
                      setSubSteps(subStepes.SUBSTEP_2);
                      setCompanyData((prevState) => ({
                        ...(prevState as LegalEntityCreateSchema),
                        companyUuid: legalEntityCheckJoinResponse?.legalEntityUuid
                      }));
                    }}
                    stepBack={() => {
                      setSteps(ModalAddCompanyModalEnum.STEP_1_START);
                      setLegalEntityCheckJoinResponse(undefined);
                    }}
                    company={legalEntityCheckJoinResponse}
                    countries={countries?.countries ?? []}
                  ></AddCompanyDetected>
                </div>
              </div>
            </>
          )}
          {steps === ModalAddCompanyModalEnum.STEP_1_NO_DETECTION && (
            <>
              <div className="mt-2 mb-4 flex w-full flex-row justify-center">
                <SectionTitle>{t("business.title_business.company_add_section")}</SectionTitle>
              </div>
              <div className="px-6 py-3">
                <Stepper origin={origin} steps={getSubStepStatus(subSteps) ?? []} />
              </div>
              <div className="px-2">
                <AddCompanyForm
                  isPendingValidation={loadingFormCompany || isLoadingAdminCreation}
                  activitiesNace={activitiesNace}
                  loadingActivitiesNace={isLoadingNace}
                  rcsCompany={rcsCompany}
                  countries={countries?.countries ?? []}
                  onValidate={async (data: LegalEntityCreateSchema | undefined) => {
                    setLoadingformCompany(true);
                    if (data?.tax_address?.tin && data.tax_address.country_id) {
                      const resp = await handleNifVerification(data?.tax_address);
                      if (resp) data.tax_address = resp;
                      else return;
                    }
                    setCompanyData(data);
                    if (isAdmin && data) {
                      const legalEntity: LegalEntityAdminCreateSchema = {
                        ...data,
                        tax_address: {
                          ...data.tax_address,
                          country: null,
                          tinExist: data.tax_address?.tinExist ?? null,
                          tin: data.tax_address?.tin ?? null,
                          tinCheck: data.tax_address?.tinCheck ?? null,
                          apiCheck: data.tax_address?.apiCheck ?? null
                        }
                      };
                      const response = await createAdminCompany(legalEntity);
                      if (response.success) {
                        setSteps(ModalAddCompanyModalEnum.STEP_3_END);
                        setCompanyResponse(response);
                        setLoadingformCompany(false);
                        return;
                      } else toast.error(t("business.error.create_company"));
                    }

                    if (!isAdmin) {
                      if (rcsCompany) {
                        const foundRCSRepresentative = await findRCSRepresentative(rcsCompany);
                        if (foundRCSRepresentative) {
                          setRepresentative(foundRCSRepresentative);
                          setSteps(ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED);
                          setSubSteps(subStepes.SUBSTEP_2);
                        } else {
                          setSteps(ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_LIST);
                          setSubSteps(subStepes.SUBSTEP_2);
                        }
                      } else {
                        setSteps(ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED);
                        setSubSteps(subStepes.SUBSTEP_2);
                      }
                    }
                    setLoadingformCompany(false);
                  }}
                />
              </div>
            </>
          )}
          {steps === ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_LIST && (
            <>
              <div className="mt-2 mb-4 flex w-full flex-row justify-center">
                <SectionTitle>{t("business.title_business.company_add_section")}</SectionTitle>
              </div>
              <div className="px-6 py-3">
                <Stepper origin={origin} steps={getSubStepStatus(subSteps) ?? []} />
              </div>
              <div className="px-2">
                <AddCompanyDetectRepresentative
                  representativeData={rcsCompany?.representatives ?? []}
                  loading={isLoadingCreation}
                  onValidate={(representative: RCSRepresentative | null) => {
                    if (representative) {
                      if (rcsCompany) {
                        setRcsCompany({
                          ...rcsCompany,
                          representatives: rcsCompany.representatives.filter(
                            (x) =>
                              x.first_name !== representative.first_name &&
                              x.last_name !== representative.last_name
                          )
                        });
                      }
                      setRepresentative(representative);
                    }
                    setSteps(ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED);
                  }}
                />
              </div>
            </>
          )}
          {steps === ModalAddCompanyModalEnum.STEP_2_REPRESENTATIVE_DETECTED && (
            <>
              <div className="mt-2 mb-4 flex w-full flex-row justify-center">
                <SectionTitle>{t("business.title_business.company_add_section")}</SectionTitle>
              </div>
              <div className="px-6 py-3">
                <Stepper origin={origin} steps={getSubStepStatus(subSteps) ?? []} />
              </div>
              <div className="px-2">
                <RepresentativeForm
                  natureMandates={natureMandates}
                  pendingNatureMandates={pendingNatureMandate}
                  representativeRcs={representative}
                  isLoadingCreation={isLoadingCreation || isLoadingJoinLegalEntity}
                  onValidate={async (data: LegalEntityLinkDto) => {
                    try {
                      if (!companyData) throw new Error("Error");

                      const payload: LegalEntityCreateSchema = {
                        ...companyData,
                        is_communication_contact: data?.mandatoryInformation.isCommunicationContact,
                        is_agent_le: data?.mandatoryInformation.isAgentLe ?? undefined,
                        agent_nature_relation:
                          data?.mandatoryInformation.agentNatureRelation ?? undefined,
                        agent_nature_relation_other:
                          data?.mandatoryInformation?.agentNatureRelationOther ?? undefined,
                        mandate_services: data?.mandatoryInformation.mandateServices ?? [],
                        agent_company_name: data?.mandatoryInformation.agentCompanyName ?? undefined
                      };

                      if (representative) {
                        payload.selectedRcsRepresentative = {
                          firstName: representative.first_name,
                          lastName: representative.last_name,
                          birthDate: representative.birth_date
                        };
                        payload.agent_nature_relation =
                          AgentNatureMandate[representative.agent_nature_relation];
                        payload.agent_nature_relation_other =
                          representative.agent_nature_relation_other;
                      }

                      let response: CompanyResponse | null = null;
                      if (companyData.companyUuid) {
                        try {
                          const joinResponse = await joinLegalEntityMutation({
                            legalEntityUuid: companyData.companyUuid,
                            agentNatureRelation: payload.agent_nature_relation ?? undefined,
                            agentNatureRelationOther: payload.agent_nature_relation_other,
                            agentCompanyName: payload.agent_company_name,
                            isAgentLe: payload.is_agent_le,
                            isCommunicationContact: payload.is_communication_contact,
                            mandateServices: payload.mandate_services
                          });
                          response = {
                            uuid: joinResponse.legalEntityUuid,
                            legal_name: joinResponse.legalName,
                            success: true,
                            home_address: {
                              Address1: joinResponse.homeAddress.address1,
                              Address2: joinResponse.homeAddress.address2,
                              Address3: joinResponse.homeAddress.address3,
                              ZipCode: joinResponse.homeAddress.zipCode,
                              Country: joinResponse.homeAddress.countryAlpha2,
                              City: joinResponse.homeAddress.city
                            }
                          };
                        } catch (error) {
                          toast.error(extractErrorDescription(error, translation));
                        }
                      } else {
                        response = await createCompany(payload);
                      }

                      if (response?.success) {
                        setSteps(ModalAddCompanyModalEnum.STEP_3_END);
                        setCompanyResponse(response);
                        setRepresentative(null);
                        setRcsCompany(undefined);
                        await refreshToken();
                      } else toast.error(t("business.error.create_company"));
                    } catch (_) {
                      toast.error(t("business.error.create_company"));
                      return;
                    }
                  }}
                />
              </div>
            </>
          )}
          {steps === ModalAddCompanyModalEnum.STEP_3_END && (
            <>
              <div className="lg: h-auto min-h-[250px] px-2">
                <FinalStepCompanAdd
                  countries={countries?.countries ?? []}
                  companyAdded={companyResponse}
                  loaderRefresh={isRefreshTokenLoading}
                  onValidate={(data) => {
                    navigate(`${Routes.companies}/${data?.uuid}/related_person`);
                    closeCompanyModal();
                    queryClient.invalidateQueries({
                      queryKey: QueryOptionsFactory.legalEntities().queryKey
                    });

                    return;
                  }}
                />
              </div>
            </>
          )}
        </div>
      </ModalContent>
    </Modal>
  );
};
